/**
 * useSorting Hook
 *
 * Manages sort field state for multi-field sorting with drag-to-reorder.
 * Converts internal sort state to API format.
 * Supports metadata-driven default sorting.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import type { DefaultSort } from '@/services/metadataService'

export interface SortField {
  /** Unique row ID for React keys */
  id: string

  /** Field name to sort by (e.g., 'modifiedAt', 'name') */
  key: string

  /** Sort direction */
  direction: 'asc' | 'desc'

  /** Null ordering (optional, advanced) */
  nulls?: 'FIRST' | 'LAST'
}

export interface ApiSortField {
  /** Field name */
  key: string

  /** Sort direction */
  direction: 'asc' | 'desc'

  /** Null ordering */
  nulls?: 'FIRST' | 'LAST'
}

export interface UseSortingOptions {
  /** Default sorts from metadata (applied on initial load) */
  defaultSortFields?: DefaultSort[]
}

export interface UseSortingResult {
  /** Current sort fields (draft state) */
  sortFields: SortField[]

  /** Set sort fields directly (for external control like drawer) */
  setSortFields: (fields: SortField[]) => void

  /** Add new sort field */
  addSort: () => void

  /** Remove sort field by ID */
  removeSort: (id: string) => void

  /** Update sort field */
  updateSort: (id: string, updates: Partial<Omit<SortField, 'id'>>) => void

  /** Reorder sort fields (after drag-and-drop) */
  reorderSort: (startIndex: number, endIndex: number) => void

  /** Clear all sort fields */
  clearSort: () => void

  /** Toggle sort direction */
  toggleDirection: (id: string) => void

  /** Apply current draft sort fields */
  applySort: () => void

  /** Revert draft sort fields to last applied state (for Cancel) */
  revertSortToApplied: () => void

  /** Convert to API format (uses applied state) */
  toApiFormat: () => ApiSortField[]

  /**
   * Applied sort fields in API format (memoized).
   * Use this instead of toApiFormat() in useMemo/useCallback dependencies
   * to avoid creating new array references on each render.
   */
  appliedApiSortFields: ApiSortField[]

  /**
   * Set applied sort fields directly, bypassing draft state.
   * Used for column header clicks where we want immediate effect.
   * Also syncs draft state to match so AF drawer shows current state.
   */
  setAppliedSortFieldsDirectly: (fields: ApiSortField[]) => void

  /** Reset sort to metadata defaults */
  resetSortToDefaults: () => void

  /** Whether metadata defaults exist */
  hasDefaultSort: boolean
}

/**
 * Maximum number of sort fields allowed
 */
const MAX_SORT_FIELDS = 5

/**
 * Convert metadata DefaultSort to SortField format
 */
function convertDefaultToSortField(defaultSort: DefaultSort): SortField {
  return {
    id: crypto.randomUUID(),
    key: defaultSort.key,
    direction: defaultSort.direction,
    nulls: defaultSort.nulls,
  }
}

/**
 * Hook to manage sort field state.
 *
 * @param options - Optional configuration including default sorts
 * @returns Sort state and actions
 */
export function useSorting(options: UseSortingOptions = {}): UseSortingResult {
  const { defaultSortFields = [] } = options

  // Convert metadata defaults to SortField format
  const metadataDefaults = useMemo(
    () => defaultSortFields.map(convertDefaultToSortField),
    [defaultSortFields]
  )

  // Initialize with defaults if available (draft state)
  const [sortFields, setSortFields] = useState<SortField[]>(() =>
    metadataDefaults.length > 0 ? [...metadataDefaults] : []
  )

  // Initialize applied state with same defaults
  const [appliedSortFields, setAppliedSortFields] = useState<SortField[]>(() =>
    metadataDefaults.length > 0 ? [...metadataDefaults] : []
  )

  // Track if we've initialized from async-loaded defaults
  const hasInitializedFromDefaults = useRef(false)

  // Sync with metadata defaults when they become available (async loading)
  // This handles the case where metadata loads after initial render
  useEffect(() => {
    if (!hasInitializedFromDefaults.current && defaultSortFields.length > 0) {
      hasInitializedFromDefaults.current = true
      const freshDefaults = defaultSortFields.map(convertDefaultToSortField)
      setSortFields(freshDefaults)
      setAppliedSortFields(freshDefaults)
    }
  }, [defaultSortFields])

  // Add new sort field
  const addSort = useCallback(() => {
    if (sortFields.length >= MAX_SORT_FIELDS) {
      return // Maximum reached
    }

    const newSort: SortField = {
      id: crypto.randomUUID(),
      key: '', // User will select
      direction: 'desc', // Common default (e.g., newest first)
    }

    setSortFields((prev) => [...prev, newSort])
  }, [sortFields.length])

  // Remove sort field
  const removeSort = useCallback((id: string) => {
    setSortFields((prev) => prev.filter((s) => s.id !== id))
  }, [])

  // Update sort field
  const updateSort = useCallback((id: string, updates: Partial<Omit<SortField, 'id'>>) => {
    setSortFields((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...updates } : s))
    )
  }, [])

  // Reorder sort fields (drag-and-drop)
  const reorderSort = useCallback((startIndex: number, endIndex: number) => {
    setSortFields((prev) => {
      const result = Array.from(prev)
      const [removed] = result.splice(startIndex, 1)
      result.splice(endIndex, 0, removed)
      return result
    })
  }, [])

  // Clear all sort fields
  const clearSort = useCallback(() => {
    setSortFields([])
  }, [])

  // Toggle direction
  const toggleDirection = useCallback((id: string) => {
    setSortFields((prev) =>
      prev.map((s) =>
        s.id === id
          ? { ...s, direction: s.direction === 'asc' ? 'desc' : 'asc' }
          : s
      )
    )
  }, [])

  // Apply current draft sort fields
  const applySort = useCallback(() => {
    setAppliedSortFields([...sortFields])
  }, [sortFields])

  // Revert draft sort fields to last applied state (for Cancel)
  const revertSortToApplied = useCallback(() => {
    setSortFields([...appliedSortFields])
  }, [appliedSortFields])

  // Convert to API format (uses applied state)
  const toApiFormat = useCallback((): ApiSortField[] => {
    return appliedSortFields
      .filter((s) => s.key) // Only include sort fields with a selected field
      .map((s) => ({
        key: s.key,
        direction: s.direction,
        ...(s.nulls && { nulls: s.nulls }), // Only include nulls if set
      }))
  }, [appliedSortFields])

  // Memoized applied sort fields in API format
  // Use this instead of calling toApiFormat() in useMemo dependencies
  // to avoid creating new array references when the function reference changes
  const appliedApiSortFields = useMemo<ApiSortField[]>(() => {
    return appliedSortFields
      .filter((s) => s.key)
      .map((s) => ({
        key: s.key,
        direction: s.direction,
        ...(s.nulls && { nulls: s.nulls }),
      }))
  }, [appliedSortFields])

  // Reset sort to metadata defaults (draft only - requires Apply to take effect)
  const resetSortToDefaults = useCallback(() => {
    // Generate fresh IDs for each default sort
    const freshDefaults = defaultSortFields.map(convertDefaultToSortField)
    setSortFields(freshDefaults)
    // NOTE: Do NOT update appliedSortFields here - user must click Apply
  }, [defaultSortFields])

  // Set applied sort fields directly, bypassing draft state.
  // Used for column header clicks where we want immediate effect.
  // Also syncs draft state to match so AF drawer shows current state.
  const setAppliedSortFieldsDirectly = useCallback((fields: ApiSortField[]) => {
    // Convert API format to internal SortField format (needs IDs for React keys)
    const sortFieldsWithIds: SortField[] = fields.map((f) => ({
      id: crypto.randomUUID(),
      key: f.key,
      direction: f.direction,
      nulls: f.nulls,
    }))

    // Update both applied and draft state so they stay in sync
    setAppliedSortFields(sortFieldsWithIds)
    setSortFields(sortFieldsWithIds)
  }, [])

  // Whether metadata defaults exist
  const hasDefaultSort = defaultSortFields.length > 0

  return {
    sortFields,
    setSortFields,
    addSort,
    removeSort,
    updateSort,
    reorderSort,
    clearSort,
    toggleDirection,
    applySort,
    revertSortToApplied,
    toApiFormat,
    appliedApiSortFields,
    setAppliedSortFieldsDirectly,
    resetSortToDefaults,
    hasDefaultSort,
  }
}
